import { useState, type ReactNode } from 'react';

import { Logo } from '@/components/primitives/Logo';
import { useTranslation } from '@/hooks/useTranslation';
import { isMobileOrTabletDevice, MOBILE_GATE_ENABLED } from '@/lib/device';
import { linkHitAreaClasses } from '@/lib/shared-variants';

import { LegalLinks } from './LegalLinks';
import { WafCaptchaDialog } from './WafCaptchaDialog';

// Figma: "SignUp and In" section, node 4795:131725 in NabatOS - Master UX Design 2026.

// `text-[length:…]` rather than `text-size-4`, which tailwind-merge would take for a colour and drop.
export const AUTH_TITLE_CLASS =
  'font-title text-[length:var(--text-size-4)] font-semibold leading-10 text-foreground';
export const AUTH_LINK_CLASS = `font-medium text-brand hover:underline disabled:opacity-60 ${linkHitAreaClasses}`;

interface AuthLayoutProps {
  /** The form. Sets its own width beside the brand panel; fills the column on phones. */
  children: ReactNode;
  /** Tell phone visitors the platform itself needs a desktop. */
  desktopNote?: boolean;
}

/**
 * The brand panel and card shared by the signup and verification screens. The panel shows from
 * `xl`: below that it and the card don't fit side by side, so the phone layout is used.
 */
export function AuthLayout({ children, desktopNote = false }: AuthLayoutProps) {
  const { t } = useTranslation();
  // The "use a desktop" note only holds while phones are kept out of the app.
  const [onMobileDevice] = useState(() => MOBILE_GATE_ENABLED && isMobileOrTabletDevice());

  return (
    <div className="flex h-dvh bg-surface-highlight xl:bg-surface-highlight-light">
      <WafCaptchaDialog />
      <aside className="hidden w-[486px] shrink-0 flex-col justify-between gap-xxxl overflow-y-auto bg-brand-hover px-[72px] pb-14 pt-16 text-foreground-inverted xl:flex">
        <Logo inverted ariaLabel="nabat.ai" className="h-[30px]" />
        <div className="flex flex-col gap-xl">
          <p className="font-title text-size-8 font-semibold leading-[72px] tracking-[-1.92px]">
            {t('The Operating System for Nature')}
          </p>
          <p className="text-size-1 leading-[26px]">
            {t(
              "An end-to-end AI platform to restore and manage the planet's most critical ecosystems at scale."
            )}
          </p>
        </div>
        <LegalLinks />
      </aside>

      {/* The body doesn't scroll (the app is a full-screen map), so the page scrolls itself. */}
      <main className="flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col items-center px-xxl pt-xxxl xl:justify-center xl:px-16 xl:py-xxxl">
          <Logo ariaLabel="nabat.ai" className="xl:hidden" />

          <div className="mt-xxl flex w-full max-w-[440px] flex-col pb-xxxl pt-[28px] xl:mt-0 xl:w-auto xl:max-w-none xl:rounded-br-[60px] xl:rounded-tl-[60px] xl:bg-surface-highlight xl:p-12">
            {children}
          </div>

          {desktopNote && onMobileDevice && (
            <p className="w-full max-w-[440px] text-size-n3 leading-4 text-shades-secondary xl:hidden">
              {t(
                'We are working hard to deliver the best mobile GIS experience. In the meantime, you can enjoy NabatOS on any desktop.'
              )}
            </p>
          )}

          <footer className="mt-auto pb-[34px] pt-xxl xl:hidden">
            <LegalLinks className="justify-center text-foreground" />
          </footer>
        </div>
      </main>
    </div>
  );
}
